import { useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import {
  cartransferSelector,
  idSelector,
} from "../../../features/cartransferSlice";
import { setCartransfer } from "../../../api/cartransferService";
import BackgroundStep from "./BackgroundStep";
import PermissionStep from "./PermissionStep";
import { Row, Col } from "antd";
import { toast } from "../../../utils/toasts";

const DocumentsStep = () => {
  const finalData = useSelector(cartransferSelector);
  const id = useSelector(idSelector);
  const lastSavedData = useRef(null);
  const saveTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);
  
  const saveCartransfer = useCallback(async (dataToSave) => {
    if (!id || !dataToSave) return;
    
    const { repertory, repertoryYear, ...noRepertory } = dataToSave;
    
    // Filtrar objetos vacíos y propiedades undefined/null
    const cleanData = Object.keys(noRepertory).reduce((acc, key) => {
      const value = noRepertory[key];
      
      // Si es un objeto, verificar si tiene propiedades
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const hasProperties = Object.keys(value).length > 0;
        if (hasProperties) {
          acc[key] = value;
        }
      } 
      // Si no es un objeto vacío, incluir el valor
      else if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      
      return acc;
    }, {});
    
    try {
      await setCartransfer(id, {
        ...cleanData,
        ...(dataToSave.repertory && { repertory: dataToSave.repertory }),
        ...(dataToSave.repertoryYear && { repertoryYear: Number(dataToSave.repertoryYear) }),
      });
      lastSavedData.current = JSON.stringify(dataToSave);
    } catch (error) {
      console.log('🔍 Error al guardar datos:', error);
      console.log('🔍 Error response:', error.response);
      console.log('🔍 Error status:', error.response?.status);
      console.log('🔍 Error data:', error.response?.data);
      
      let errorMessage = "Error al guardar datos";
      
      if (error.response?.status === 401) {
        errorMessage = "Sesión expirada. Por favor, inicia sesión nuevamente.";
      } else if (error.response?.status === 403) {
        errorMessage = "No tienes permisos para realizar esta acción.";
      } else if (error.response?.status === 404) {
        errorMessage = "No se encontró el repertorio. Por favor, recarga la página.";
      } else if (error.response?.status === 500) {
        errorMessage = "Error del servidor. Por favor, intenta nuevamente.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast("error", errorMessage);
    }
  }, [id]);
  
  useEffect(() => {
    if (!id || !finalData) return;
    
    // Evitar guardar en el montaje inicial
    if (isInitialMount.current) {
      isInitialMount.current = false;
      lastSavedData.current = JSON.stringify(finalData);
      return;
    }
    
    // Evitar guardar si los datos no han cambiado
    const currentDataString = JSON.stringify(finalData);
    if (lastSavedData.current === currentDataString) return;
    
    // Evitar guardar si solo cambió el repertorio (asignación automática)
    const lastData = lastSavedData.current ? JSON.parse(lastSavedData.current) : null;
    if (lastData) {
      const { repertory: lastRepertory, repertoryYear: lastRepertoryYear, ...lastDataWithoutRepertory } = lastData;
      const { repertory: currentRepertory, repertoryYear: currentRepertoryYear, ...currentDataWithoutRepertory } = finalData;
      
      // Si solo cambió el repertorio, no guardar
      if (JSON.stringify(lastDataWithoutRepertory) === JSON.stringify(currentDataWithoutRepertory)) {
        lastSavedData.current = currentDataString;
        return;
      }
    }
    
    // Limpiar timeout anterior
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce: esperar 2 segundos antes de guardar (aumentado para reducir llamadas)
    saveTimeoutRef.current = setTimeout(() => {
      saveCartransfer(finalData);
    }, 2000);
    
    // Cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [id, finalData, saveCartransfer]);
  return (
    <Row gutter={[20, 16]} align="top">
      <Col span={12} xl={12} xs={24}>
        <BackgroundStep label="Cert. Antecedentes" />
      </Col>
      <Col span={12} xl={12} xs={24}>
        <PermissionStep label="Permiso de circulación" />
      </Col>
    </Row>
  );
};

export default DocumentsStep;
